// Écran Chartes — la fiche (docs/REFONTE-CHARTES.md §2). Formulaire calme, sans éditeur : l'aperçu
// et le bouton vers l'éditeur plein écran, le tableau des styles par rôle (la seule source de
// couleur, taille, graisse de la signature), l'identité, les images, les coordonnées et réseaux.
// Routes : #chartes, #chartes/<id>, #chartes/<id>/role/<rôle> (fiche ouverte sur cette ligne du
// tableau), #chartes/<id>/mise-en-page (éditeur, dans layout-editor.ts).
import type { Brand, FontWeight, RoleId, TextRoleStyle } from '../../../supabase/functions/_shared/core/index.ts';
import {
  CUSTOM_ROLE_NAME_MAX,
  customRoleUsage,
  effectiveLayout,
  fitLayoutWidths,
  effectiveStyles,
  fontStackOptions,
  newCustomRoleId,
  removeCustomRole,
  FONT_WEIGHTS,
  GIF_FIRST_FRAME_NOTE,
  LETTER_SPACING_OUTLOOK_NOTE,
  LETTER_SPACING_WARN_BELOW,
  LINKEDIN_COLOR_NOTE,
  LOGO_MAX_DISPLAY_HEIGHT,
  renderSignature,
  ROLE_HINTS,
  ROLE_IDS,
  ROLE_LABELS,
  WEIGHT_LABELS,
} from '../../../supabase/functions/_shared/core/index.ts';
import { uploadTintedIcons } from '../tint.ts';
import { busy, field, h, mount, toast } from '../dom.ts';
import { domainOf } from '../../../supabase/functions/_shared/core/index.ts';
import { publicBaseUrl } from '../config.ts';
import { reload, type Store } from '../state.ts';
import { signatureFrame } from './sigframe.ts';
import { signatureMeters } from './meters.ts';
import { assetInput } from './upload.ts';

const EMPTY: Brand = {
  id: '',
  name: '',
  domains: [],
  logo_url: null,
  logo_width: 145,
  logo_height: null,
  primary_color: '#000000',
  accent_color: '#7e8c8d',
  font_family: 'Arial, Helvetica, sans-serif',
  website_url: null,
  instagram_url: null,
  facebook_url: null,
  linkedin_url: null,
  tiktok_url: null,
  address: null,
  phone: null,
  legal_mention: null,
  layout: 'classic',
  is_default: false,
  name_color: null,
  title_color: null,
  body_color: null,
  social_color: '#000000',
  separator_style: 'line',
  separator_color: null,
  separator_width: 1,
  separator_gap: 16,
  logo_position: 'left',
  blocks: null,
  layout_tree: null,
  styles: null,
};

/** Identifiant technique dérivé du nom du lieu (minuscules, sans accent, tirets), unique parmi les chartes. */
function slugFrom(name: string, taken: string[]): string {
  const base = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'charte';
  let id = base;
  for (let n = 2; taken.includes(id); n++) id = `${base}-${n}`;
  return id;
}

/** Sous cette largeur de fenêtre, la liste devient un sélecteur et les sections 4 et 5 se replient. */
const NARROW_PAGE = 900;
const narrowPage = () => window.innerWidth < NARROW_PAGE;

/** Une section repliable sous 900 px, toujours ouverte au-dessus (docs/REFONTE-CHARTES.md §2). */
function section(title: string, foldable: boolean, ...body: (HTMLElement | null)[]): HTMLElement {
  if (!foldable) return h('section', { class: 'sheet-section' }, h('h3', { class: 'section-title' }, title), ...body);
  const d = h('details', { class: 'sheet-section fold', open: !narrowPage() }, h('summary', null, h('h3', { class: 'section-title' }, title)), ...body);
  window.addEventListener('resize', () => {
    if (!narrowPage()) d.open = true;
  });
  return d;
}

export function brandsView(store: Store, refresh: () => void): HTMLElement {
  const root = h('div', { class: 'split sheet' });
  const list = h('div', { class: 'panel side' });
  const editor = h('div', { class: 'panel' });
  const route = /^chartes(?:\/([a-z0-9-]+))?(?:\/role\/([a-z0-9-]+))?$/.exec(location.hash.replace('#', ''));
  const wantedId = route?.[1] ?? null;
  const wantedRole = route?.[2] ?? null;

  const open = (b: Brand | null, focusRole: string | null = null) => {
    const isNew = !b;
    const cur: Brand = structuredClone(b ?? EMPTY);
    // Les styles par rôle sont toujours explicites dans la fiche (dérivés des anciennes colonnes s'ils
    // manquent) : ce qu'on voit dans le tableau est exactement ce qui sera enregistré.
    cur.styles = effectiveStyles(cur);
    const styles = cur.styles;
    /** Photo de la fiche telle qu'ouverte (ou enregistrée) : sert à détecter les modifications non enregistrées. */
    const saved = JSON.stringify(cur);
    const preview = h('div', { class: 'mail' });
    const meters = signatureMeters();
    const sample = store.people.find((p) => p.kind === 'person') ?? store.people[0];
    const redraw = () => {
      if (!sample) return mount(preview, h('p', { class: 'muted' }, 'Ajoutez une personne pour voir l’aperçu.'));
      const probe = { ...cur, id: cur.id || '__new', domains: ['example.test'] };
      const r = renderSignature({ person: { ...sample, brand_id: probe.id }, sendAsEmail: sample.primary_email, brands: [probe], campaigns: [] }, { mode: 'gmail', publicBaseUrl });
      mount(preview, signatureFrame(r.html));
      meters.update(r.html);
    };
    const bind = <K extends keyof Brand>(
      key: K,
      input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
      parse: (v: string) => Brand[K] = (v) => (v.trim() || null) as Brand[K],
    ) => {
      input.addEventListener('input', () => {
        cur[key] = parse(input.value);
        redraw();
      });
      return input;
    };
    const txt = (key: keyof Brand, ph = '') => bind(key, h('input', { value: (cur[key] as string) ?? '', placeholder: ph }));

    // ------------------------------------------------------------ 2. Styles : un tableau, six rôles
    const hex6 = (c: string) => (/^#[0-9a-f]{3}$/i.test(c) ? '#' + [...c.slice(1)].map((x) => x + x).join('') : c);
    const num = (v: string): number | null => {
      const n = Number(v.replace(',', '.'));
      return v.trim() === '' || !Number.isFinite(n) ? null : n;
    };
    const lsNotes = h('p', { class: 'muted small' });
    const refreshNotes = () => {
      const low = (['name', 'title', 'body', 'legal'] as const).filter((r) => styles[r].letterSpacing < LETTER_SPACING_WARN_BELOW);
      lsNotes.textContent = low.length ? `${low.map((r) => ROLE_LABELS[r]).join(', ')} : ${LETTER_SPACING_OUTLOOK_NOTE}` : '';
    };
    const colorCell = (get: () => string, set: (v: string) => void) => {
      const code = h('code', null, get());
      return h('span', { class: 'style-color' }, h('input', { type: 'color', value: hex6(get()), oninput: (e: Event) => { set((e.target as HTMLInputElement).value); code.textContent = get(); redraw(); } }), code);
    };
    const numberCell = (get: () => number | null, set: (v: number | null) => void, attrs: Record<string, unknown>) =>
      h('input', { type: 'number', value: get() ?? '', ...attrs, oninput: (e: Event) => { set(num((e.target as HTMLInputElement).value)); refreshNotes(); redraw(); } });
    const dash = () => h('span', { class: 'muted' }, '—');
    const textCells = (s: TextRoleStyle) => [
      colorCell(() => s.color, (v) => (s.color = v)),
      numberCell(() => s.size, (v) => (s.size = Math.round(v ?? s.size)), { min: 6, max: 60, step: 1 }),
      h('select', { onchange: (e: Event) => { s.weight = (e.target as HTMLSelectElement).value as FontWeight; redraw(); } }, FONT_WEIGHTS.map((w) => h('option', { value: w, selected: w === s.weight }, WEIGHT_LABELS[w]))),
      h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: !!s.italic, onchange: (e: Event) => { s.italic = (e.target as HTMLInputElement).checked; redraw(); } }), 'Italique'),
      numberCell(() => s.letterSpacing, (v) => (s.letterSpacing = v ?? 0), { min: -3, max: 10, step: 0.1, title: 'px, jamais em' }),
      numberCell(() => s.lineHeight, (v) => (s.lineHeight = v ? Math.round(v) : null), { min: 6, max: 80, step: 1, placeholder: 'auto', title: 'px ; vide = 1,4 × la taille' }),
    ];
    const roleRow = (role: RoleId) => {
      const cells: HTMLElement[] = [];
      if (role === 'social' || role === 'separator') {
        const s = styles[role];
        cells.push(colorCell(() => s.color, (v) => (s.color = v)));
        cells.push(role === 'social' ? numberCell(() => styles.social.size, (v) => (styles.social.size = Math.round(v ?? 24)), { min: 12, max: 48, step: 1 }) : dash());
        cells.push(dash(), dash(), dash(), dash());
      } else cells.push(...textCells(styles[role]));
      const label = h('th', { scope: 'row' }, ROLE_LABELS[role], h('small', { class: 'muted' }, ROLE_HINTS[role]));
      return h('tr', { class: `role-row${focusRole === role ? ' focus' : ''}`, 'data-role': role }, label, ...cells.map((c) => h('td', null, c)), h('td', null));
    };
    // Rôles personnalisés (« Ajouter un style ») : identifiant stable x-…, nom libre, mêmes propriétés ;
    // suppression refusée tant qu'un bloc de texte libre de la mise en page l'utilise.
    const customRow = (id: string) => {
      const s = styles.custom![id];
      const name = h('input', { value: s.name, maxlength: CUSTOM_ROLE_NAME_MAX, class: 'role-name', placeholder: 'Nom du style', oninput: (e: Event) => { s.name = (e.target as HTMLInputElement).value; } });
      const used = customRoleUsage(effectiveLayout(cur), id);
      const del = h('button', {
        type: 'button',
        class: 'ghost danger',
        title: used.length ? `Utilisé par ${used.length} bloc${used.length > 1 ? 's' : ''} de texte libre : ${used.map((u) => u.where).join(' ; ')}` : 'Supprimer ce style',
        onclick: () => {
          const r = removeCustomRole(styles, effectiveLayout(cur), id);
          if (!r.ok) return toast(r.reason, 'error');
          delete styles.custom![id];
          if (!Object.keys(styles.custom!).length) delete styles.custom;
          drawStyles();
          redraw();
        },
      }, 'Supprimer');
      const label = h('th', { scope: 'row' }, name, h('small', { class: 'muted' }, used.length ? `Utilisé par ${used.length} bloc${used.length > 1 ? 's' : ''} de texte libre` : 'Style personnalisé, choisi par les blocs de texte libre'));
      return h('tr', { class: `role-row custom${focusRole === id ? ' focus' : ''}`, 'data-role': id }, label, ...textCells(s).map((c) => h('td', null, c)), h('td', { class: 'actions' }, del));
    };
    const tbody = h('tbody');
    const addBtn = h('button', {
      type: 'button',
      class: 'ghost',
      onclick: () => {
        const id = newCustomRoleId(styles);
        styles.custom = { ...(styles.custom ?? {}), [id]: { ...styles.body, name: 'Nouveau style' } };
        drawStyles();
        tbody.querySelector<HTMLInputElement>(`tr[data-role="${id}"] input.role-name`)?.select();
      },
    }, '+ Ajouter un style');
    const drawStyles = () => mount(tbody, ROLE_IDS.map(roleRow), Object.keys(styles.custom ?? {}).map(customRow));
    const stylesTable = h(
      'div',
      { class: 'table-wrap styles-wrap' },
      h(
        'table',
        { class: 'styles' },
        h('thead', null, h('tr', null, h('th', null, 'Rôle'), h('th', null, 'Couleur'), h('th', null, 'Taille (px)'), h('th', null, 'Graisse'), h('th', null, 'Italique'), h('th', null, 'Interlettrage (px)'), h('th', null, 'Interligne (px)'), h('th', null))),
        tbody,
      ),
      h('div', { class: 'row' }, addBtn, h('small', { class: 'muted' }, 'Un style personnalisé se nomme librement et se choisit dans un bloc de texte libre, par son nom. Le renommer ne casse rien ; le supprimer est refusé tant qu’un bloc l’utilise.')),
    );
    drawStyles();
    refreshNotes();

    // ------------------------------------------------------------ 0. Quelles adresses utilisent cette charte
    const otherDomains = new Set(store.brands.filter((x) => x.id !== cur.id).flatMap((x) => x.domains));
    const coverage = h('small', { class: 'coverage' });
    const drawCoverage = () => {
      const parts = cur.domains.map((d) => {
        const people = store.people.filter((p) => domainOf(p.primary_email) === d);
        const managed = people.filter((p) => p.managed && p.active).length;
        return `${people.length} adresse${people.length > 1 ? 's' : ''} en @${d}, dont ${managed} gérée${managed > 1 ? 's' : ''}`;
      });
      if (cur.is_default) {
        const orphans = store.people.filter((p) => !otherDomains.has(domainOf(p.primary_email)) && !cur.domains.includes(domainOf(p.primary_email)));
        parts.push(`charte par défaut : ${orphans.length} adresse${orphans.length > 1 ? 's' : ''} sans domaine reconnu`);
      }
      coverage.textContent = parts.length ? parts.join(' · ') : 'Aucun domaine : aucune adresse ne prend cette charte pour l’instant.';
    };
    const domains = bind('domains', h('input', { value: cur.domains.join(', '), placeholder: 'badaboum.paris' }), (v) => v.split(/[\s,;]+/).map((d) => d.trim().toLowerCase()).filter(Boolean));
    domains.addEventListener('input', drawCoverage);
    const isDefault = h('input', { type: 'checkbox', checked: cur.is_default, onchange: (e: Event) => { cur.is_default = (e.target as HTMLInputElement).checked; drawCoverage(); } });
    drawCoverage();

    // ------------------------------------------------------------ 3. Identité : l'identifiant est dérivé du nom, en lecture seule
    const idView = h('input', { value: cur.id, readonly: true, class: 'readonly' });
    const nameInput = bind('name', h('input', { value: cur.name, placeholder: 'BADABOUM' }));
    if (isNew) {
      nameInput.addEventListener('input', () => {
        cur.id = slugFrom(cur.name ?? '', store.brands.map((x) => x.id));
        idView.value = cur.id;
      });
    }

    // ------------------------------------------------------------ 4. Images
    const logoW = bind('logo_width', h('input', { type: 'number', min: 40, max: 300, value: cur.logo_width }), (v) => Number(v) || 145);
    /** La mise en page enregistrée déclare-t-elle la colonne du logo à une autre largeur que celle du logo ? Dit tout de suite, pas trois semaines plus tard. */
    const widthNote = h('small', { class: 'warn' });
    const refreshWidthNote = () => {
      if (isNew || !cur.layout_tree) return (widthNote.textContent = '');
      const fit = fitLayoutWidths(cur, effectiveStyles(cur), effectiveLayout(cur));
      const low = fit.corrections.filter((c) => c.kind === 'raise');
      widthNote.textContent = low.length
        ? `La mise en page enregistrée déclare ${low.map((c) => `la colonne ${c.colIndex} de la ligne ${c.rowIndex} à ${c.from} px pour un contenu de ${c.to} px`).join(' ; ')}. Le rendu corrige de lui-même ; pour mettre la mise en page d’équerre, ouvrez « Modifier la mise en page » puis « Terminer ».`
        : '';
    };
    logoW.addEventListener('input', refreshWidthNote);
    setTimeout(refreshWidthNote, 0);
    const logoH = bind('logo_height', h('input', { type: 'number', min: 20, max: 300, value: cur.logo_height ?? '' }), (v) => (v ? Number(v) : null));
    const logo = assetInput(store.api, 'logos', cur.logo_url, (u, img, plan) => {
      cur.logo_url = u || null;
      if (plan?.kind === 'gif') {
        // Dimensions d'affichage calculées par le cœur (hauteur ≤ 78 px, ratio conservé) ; le fichier est intact.
        cur.logo_width = plan.logo_width;
        cur.logo_height = plan.logo_height;
        logoW.value = String(cur.logo_width);
        logoH.value = String(cur.logo_height);
      } else if (img?.naturalWidth) {
        cur.logo_height = Math.round((cur.logo_width * img.naturalHeight) / img.naturalWidth);
        logoH.value = String(cur.logo_height);
      }
      redraw();
    }, { brandId: () => cur.id || 'charte' });

    // ------------------------------------------------------------ enregistrer
    const save = h('button', null, 'Enregistrer');
    save.addEventListener('click', () =>
      busy(save, async () => {
        if (!/^[a-z0-9-]+$/.test(cur.id)) throw new Error('Identifiant : minuscules, chiffres et tirets uniquement');
        if (!cur.name) throw new Error('Nom obligatoire');
        for (const [id, s] of Object.entries(styles.custom ?? {})) {
          if (!s.name.trim()) throw new Error('Un style personnalisé n’a pas de nom : nommez-le ou supprimez-le.');
          s.name = s.name.trim();
          void id;
        }
        // Icônes réseaux teintées : générées ici, à partir des PNG servis avec le back-office
        // (même origine que la page), avant d'enregistrer la charte qui les référence.
        const icons = store.api.demo ? 0 : await uploadTintedIcons(store.api, cur, location.origin);
        await store.api.saveBrand(cur);
        await reload(store);
        toast(store.api.demo ? 'Charte enregistrée (démo)' : `Charte enregistrée — appliquée à la prochaine poussée (≤ 15 min)${icons ? ` ; ${icons} icône(s) teintée(s) générée(s)` : ''}`);
        if (isNew) location.hash = `#chartes/${cur.id}`;
        else refresh();
      }));

    // Sous ~900 px de fenêtre, la liste des chartes disparaît : ce sélecteur la remplace en haut de la fiche
    const picker = h(
      'select',
      {
        class: 'brand-picker',
        title: 'Changer de charte',
        onchange: (e: Event) => {
          const v = (e.target as HTMLSelectElement).value;
          location.hash = v === '__new' ? '#chartes/nouvelle' : `#chartes/${v}`;
        },
      },
      store.brands.map((x) => h('option', { value: x.id, selected: !isNew && x.id === cur.id }, x.name)),
      h('option', { value: '__new', selected: isNew }, '＋ Nouvelle charte'),
    );
    const nameField = field('Nom du lieu', nameInput, 'Nomme la charte ici et sert de texte alternatif au logo dans les mails.');
    const editBtn = isNew
      ? h('button', { disabled: true, title: 'Enregistrez d’abord la charte' }, 'Modifier la mise en page')
      : h('a', {
        class: 'button',
        href: `#chartes/${cur.id}/mise-en-page`,
        // Le lien quitte la fiche sans l'enregistrer : un style ajouté et non enregistré serait perdu
        // en silence (et absent de la liste des rôles dans l'éditeur). On bloque et on le dit.
        onclick: (e: Event) => {
          if (JSON.stringify(cur) === saved) return;
          e.preventDefault();
          const before = (JSON.parse(saved) as Brand).styles?.custom ?? {};
          const news = Object.entries(styles.custom ?? {}).filter(([id]) => !before[id]).map(([, s]) => `« ${s.name} »`);
          toast(`Enregistrez d’abord la fiche : elle a des modifications non enregistrées${news.length ? ` (dont le style ${news.join(', ')})` : ''}. La mise en page s’ouvre ensuite.`, 'error');
        },
      }, 'Modifier la mise en page');

    const foldable = narrowPage();
    mount(
      editor,
      h('div', { class: 'toolbar' }, h('h2', null, isNew ? 'Nouvelle charte' : cur.name), picker, save),
      // 0. La règle d'affectation : le champ le plus conséquent de l'écran, en tête
      h(
        'section',
        { class: 'sheet-section sheet-domains' },
        h('h3', { class: 'section-title' }, 'Quelles adresses utilisent cette charte'),
        field('Domaines mail', domains, 'Séparés par des virgules. Toute adresse d’envoi de ces domaines reçoit cette signature.'),
        coverage,
        h('label', { class: 'checks field' }, isDefault, ' Charte par défaut : aussi pour les adresses dont le domaine n’est rattaché à aucune charte'),
      ),
      // 1. Aperçu + bouton vers l'éditeur + compteurs
      h(
        'section',
        { class: 'sheet-section sheet-preview' },
        preview,
        h(
          'div',
          { class: 'preview-side' },
          editBtn,
          meters.el,
          h('small', { class: 'muted' }, isNew ? 'La mise en page s’édite une fois la charte enregistrée.' : 'Colonnes, blocs, traits, marges : la structure. Le style vient du tableau ci-dessous.'),
        ),
      ),
      // 2. Styles — un tableau, la police sur la ligne de titre
      h(
        'section',
        { class: 'sheet-section' },
        h(
          'div',
          { class: 'section-head' },
          h('h3', { class: 'section-title' }, 'Styles', h('small', null, 'Le style appartient à la charte, par rôle : un bloc ne peut pas y déroger.')),
          h(
            'label',
            { class: 'font-field' },
            h('span', { class: 'field-label' }, 'Police'),
            bind('font_family', h('select', null, fontStackOptions(cur.font_family).map((f) => h('option', { value: f.css, selected: f.css === (cur.font_family ?? '') }, f.label)))),
            h('small', null, 'Liste fermée : polices présentes chez tous les destinataires, pour tous les rôles, nom compris.'),
          ),
        ),
        stylesTable,
        lsNotes,
        h('small', { class: 'muted' }, `Icônes réseaux : noir = icônes communes ; autre teinte = variantes générées à l’enregistrement. ${LINKEDIN_COLOR_NOTE}`),
      ),
      // 3. Identité
      section(
        'Identité',
        false,
        h(
          'div',
          { class: 'grid2' },
          nameField,
          h('details', { class: 'field advanced' }, h('summary', null, 'Avancé'), field('Identifiant technique (lecture seule)', idView, isNew ? 'Généré à partir du nom du lieu à la création.' : 'Fixé à la création ; sert de clé en base et dans les URL des icônes teintées.')),
        ),
      ),
      // 4. Images
      section(
        'Images',
        foldable,
        h(
          'div',
          { class: 'grid2' },
          field('Logo', logo.el, `PNG sur fond transparent exporté en 2× la taille affichée, ou GIF animé (stocké tel quel, hauteur d’affichage ≤ ${LOGO_MAX_DISPLAY_HEIGHT} px). ${GIF_FIRST_FRAME_NOTE}`),
          h('div', { class: 'grid2 tight' }, field('Largeur (px)', logoW), field('Hauteur (px)', logoH)),
          widthNote,
          h('div', { class: 'field' }, h('span', { class: 'field-label' }, 'Bannière'), h('small', null, 'Les bannières sont des campagnes (onglet Campagnes). Une bannière de fond permanente est une campagne sans dates, en priorité basse, ciblant ce lieu : c’est elle qui s’affiche quand aucun événement ne tourne.'), h('a', { class: 'button ghost', href: '#campagnes' }, 'Ouvrir les campagnes')),
        ),
      ),
      // 5. Coordonnées et réseaux
      section(
        'Coordonnées et réseaux',
        foldable,
        h(
          'div',
          { class: 'grid2' },
          field('Téléphone standard', txt('phone')),
          field('Adresse', txt('address', '12 rue …, 75011 Paris')),
          field('Site web', txt('website_url', 'https://'), 'Lien du logo, de la ligne « Site web » et de l’icône « web » (en tête des réseaux, présente par défaut, retirable dans la mise en page).'),
          field('Mention légale', bind('legal_mention', h('textarea', { rows: 2 }, cur.legal_mention ?? '')), 'Ex. mention Loi Évin si la bannière montre de l’alcool'),
          field('Instagram', txt('instagram_url', 'https://www.instagram.com/…')),
          field('Facebook', txt('facebook_url', 'https://')),
          field('TikTok', txt('tiktok_url', 'https://')),
          field('LinkedIn', txt('linkedin_url', 'https://')),
        ),
      ),
    );
    redraw();
    if (focusRole) {
      const row = editor.querySelector<HTMLElement>(`tr[data-role="${focusRole}"]`);
      requestAnimationFrame(() => row?.scrollIntoView({ block: 'center' }));
      row?.querySelector('input')?.focus();
    }
  };

  mount(
    list,
    h('div', { class: 'toolbar' }, h('h2', null, 'Chartes'), h('a', { class: 'button ghost', href: '#chartes/nouvelle', title: 'Nouvelle charte' }, '+')),
    h(
      'ul',
      { class: 'list' },
      store.brands.map((b) =>
        h(
          'li',
          { class: b.id === wantedId ? 'current' : '' },
          h(
            'a',
            { class: 'link', href: `#chartes/${b.id}` },
            h('span', { class: 'swatch', style: `background:${effectiveStyles(b).title.color}` }),
            b.name,
            b.is_default ? h('span', { class: 'chip small' }, 'défaut') : null,
            h('small', { class: 'muted' }, b.domains.join(', ') || 'aucun domaine'),
          ),
        )
      ),
    ),
  );
  mount(root, list, editor);
  const wanted = wantedId === 'nouvelle' ? null : store.brands.find((b) => b.id === wantedId) ?? store.brands[0] ?? null;
  if (wanted || wantedId === 'nouvelle') open(wanted, wantedRole);
  else mount(editor, h('p', { class: 'muted' }, 'Aucune charte.'), h('a', { class: 'button', href: '#chartes/nouvelle' }, 'Créer une charte'));
  return root;
}
